import { GENDER } from '../../../domain/domainCodes.js'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Building2, IdCard, KeyRound, Save, UserRound } from 'lucide-react'
import Avatar from '../../../components/Avatar'
import Button from '../../../components/Button'
import FormRow from '../../../components/FormRow'
import PageLoader from '../../../components/PageLoader'
import Select from '../../../components/Select'
import { EmptyState } from '../../../components/States'
import TextField from '../../../components/TextField'
import { useToast } from '../../../components/toastContext'
import PortalIntro from '../../components/PortalIntro'
/* The rows' rhythm travels with the markup (see PortalForm.css): the admin's own
   /profile renders this page outside the portal layout that also imports it. */
import '../../components/PortalForm.css'
import { useResource } from '../../../lib/useResource'
import { getProfilePage, updateProfile } from './profileService'
import * as S from '../../strings'
import './ProfilePage.css'
import { serverDetail, serverText } from '../../../lib/i18n'
import { useSession } from '../../../lib/sessionContext'

/**
 * الملف الشخصي.
 *
 * ONE FORM, READ TOP TO BOTTOM. It was a responsive grid of three or four cards, which meant the
 * reading order changed with the width of the window — at a wide viewport the eye crossed from
 * Arabic name to email and back to employee number, and at a narrow one it did not — and each card
 * had to be padded to the height of the tallest beside it. The fields are a single column now, in
 * one order, with the segments as headings inside the one card rather than as separate panels.
 *
 * The segments are built as data, so a segment whose every field the client has hidden is not
 * rendered at all. Three of the four used to render their heading regardless, so a client that
 * hid the two names and the gender got an empty "البيانات الشخصية" card above the rest.
 *
 * WHICH FIELDS EXIST IS THE CLIENT'S DECISION. The payload carries a visible/editable pair for
 * each of the nine standard fields and each of the five custom attributes, and every input below
 * is behind its own pair — a hidden field is not rendered, and a visible-but-locked one is
 * rendered disabled rather than left out, because "you have an employee number and cannot change
 * it" is different from "you have none". §79.
 */
export default function ProfilePage() {
  const toast = useToast()
  const navigate = useNavigate()
  const { revalidate } = useSession()
  const { data, loading } = useResource((signal) => getProfilePage(signal), [])

  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(false)

  /* Seeded from the load, not at first render — `useState` keeps its FIRST argument, which would
     be the empty form the screen draws before the payload lands. */
  useEffect(() => {
    if (!data) return
    setForm({
      nameAr: data.nameAr,
      nameEn: data.nameEn,
      gender: data.gender == null ? '' : String(data.gender),
      email: data.email,
      phone: data.phone,
      mobile: data.mobile,
      employeeId: data.employeeId,
      departmentId: String(data.departmentId ?? ''),
      ...Object.fromEntries(data.attributes.map((item) => [item.key, item.value])),
    })
  }, [data])

  const departments = useMemo(
    () => (data?.departments ?? []).map((group) => ({ value: String(group.id), label: group.name })),
    [data],
  )

  const departmentName = useMemo(
    () => data?.departments.find((group) => String(group.id) === String(data?.departmentId))?.name ?? '',
    [data],
  )

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }))

  const save = async (event) => {
    event.preventDefault()
    if (busy || !form) return

    setBusy(true)
    try {
      const result = await updateProfile(form)
      if (!result.ok) {
        toast.error(serverDetail(result.message) || S.profile.saved)
        return
      }
      toast.success(serverText(result.message) || S.profile.saved)
      await revalidate()
      /* The server may ask to be followed somewhere after a save — the legacy obeys it with
         `location.href`. Same destination, without leaving the app. */
      if (result.redirectTo) navigate(`/${String(result.redirectTo).replace(/^\/+/, '')}`)
    } catch (error) {
      toast.error(error?.message ?? '')
    } finally {
      setBusy(false)
    }
  }

  if (loading || !form || !data) {
    return (
      <>
        <PortalIntro art="profile" icon={UserRound} title={S.profile.title} />
        <PageLoader />
      </>
    )
  }

  const shown = (key) => data.fields[key]?.visible
  const locked = (key) => !data.fields[key]?.editable
  const displayName = (shown('nameAr') && data.nameAr) || (shown('nameEn') && data.nameEn) || S.profile.title
  const canEdit = Object.entries(data.fields).some(([key, field]) => key !== 'username' && field.visible && field.editable)
    || data.attributes.some(field => field.visible && field.editable)

  /** A plain text field, bound to the form and disabled when the client locked it. */
  const text = (key, extra) => (
    <TextField value={form[key] ?? ''} onChange={set(key)} disabled={locked(key)} {...extra} />
  )

  /* The segments, in reading order. `rows` is filtered before the segment is, so a segment with
     nothing left to show goes with its heading. */
  const segments = [
    {
      id: 'personal',
      title: S.profile.personal,
      rows: [
        /* The note about the certificate belongs to the name field, not to the segment — as a
           subtitle it is a line of prose above a form, which is the one place nobody reads. */
        shown('nameAr') && (
          <FormRow key="nameAr" label={S.profile.arabicName} hint={S.profile.nameNote}>
            {text('nameAr')}
          </FormRow>
        ),
        shown('nameEn') && (
          <FormRow key="nameEn" label={S.profile.englishName}>
            {text('nameEn', { dir: 'ltr' })}
          </FormRow>
        ),
        shown('gender') && (
          <FormRow key="gender" label={S.profile.gender}>
            <Select
              value={form.gender}
              onChange={set('gender')}
              disabled={locked('gender')}
              options={[
                { value: String(GENDER.MALE), label: S.profile.male },
                { value: String(GENDER.FEMALE), label: S.profile.female },
              ]}
            />
          </FormRow>
        ),
      ],
    },
    {
      id: 'account',
      title: S.profile.account,
      /* No leading glyph on these three: the icon is placed against the page's inline-start
         while `dir="ltr"` moves the text to the opposite edge, so the two collide. */
      rows: [
        shown('email') && (
          <FormRow key="email" label={S.profile.email}>
            {text('email', { type: 'email', dir: 'ltr' })}
          </FormRow>
        ),
        shown('mobile') && (
          <FormRow key="mobile" label={S.profile.mobile}>
            {text('mobile', { dir: 'ltr' })}
          </FormRow>
        ),
        shown('phone') && (
          <FormRow key="phone" label={S.profile.phone}>
            {text('phone', { dir: 'ltr' })}
          </FormRow>
        ),
      ],
    },
    {
      id: 'work',
      title: S.profile.work,
      rows: [
        shown('username') && (
          /* Always locked in practice — `/Update` has no username field to send it in. */
          <FormRow key="username" label={S.profile.username}>
            <TextField value={data.username} disabled dir="ltr" />
          </FormRow>
        ),
        shown('employeeId') && (
          <FormRow key="employeeId" label={S.profile.employeeId}>
            {text('employeeId', { dir: 'ltr' })}
          </FormRow>
        ),
        shown('departmentId') && departments.length > 0 && (
          <FormRow key="departmentId" label={S.profile.department}>
            <Select
              value={form.departmentId}
              onChange={set('departmentId')}
              options={departments}
              disabled={locked('departmentId')}
            />
          </FormRow>
        ),
      ],
    },
    {
      /* The five the client names for itself. They carry their own pair rather than one from
         `data.fields`, and their label is whatever the client called them. */
      id: 'more',
      title: S.shell.more,
      rows: data.attributes
        .filter((item) => item.visible)
        .map((item) => (
          <FormRow key={item.key} label={item.label}>
            <TextField
              value={form[item.key] ?? ''}
              onChange={set(item.key)}
              disabled={!item.editable}
            />
          </FormRow>
        )),
    },
  ]
    .map((segment) => ({ ...segment, rows: segment.rows.filter(Boolean) }))
    .filter((segment) => segment.rows.length > 0)

  return (
    <>
      <PortalIntro art="profile" icon={UserRound} title={S.profile.title} />

      {/* WHO YOU ARE BESIDE WHAT YOU CAN CHANGE (2026-10-28): the identity is a card of its
          own at the start of the row — the avatar, the name, the two facts and the password —
          and the form fills the rest, two fields to a line, with its Save in its own foot. */}
      <div className="profile">
        <section className="me">
          <Avatar name={displayName} size="lg" solid />
          <div className="me__text">
            <h2 className="me__name">
              <bdi>{displayName}</bdi>
            </h2>
            {shown('username') && <p className="me__user" dir="ltr">
              {data.username}
            </p>}
          </div>

          {/* Two facts, not three: there is no start date in this payload — `ProfileData` carries
              no dates at all. */}
          {(shown('departmentId') || shown('employeeId')) && <dl className="me__facts">
            {shown('departmentId') && <div>
              <dt>
                <Building2 size={13} strokeWidth={2} aria-hidden="true" />
                {S.profile.department}
              </dt>
              <dd>
                <bdi>{departmentName || '—'}</bdi>
              </dd>
            </div>}
            {shown('employeeId') && <div>
              <dt>
                <IdCard size={13} strokeWidth={2} aria-hidden="true" />
                {S.profile.employeeId}
              </dt>
              <dd className="tabular">{data.employeeId || '—'}</dd>
            </div>}
          </dl>}

          <Button as={Link} to="/portal/profile/password" variant="secondary" size="sm" className="me__password">
            <KeyRound size={15} strokeWidth={2} />
            {S.profile.changePassword}
          </Button>
        </section>

        {/* Every field hidden. Without this the page is a heading, an empty card and a Save button
            that posts nothing — so it says so instead. */}
        {segments.length === 0 ? (
          <EmptyState icon={UserRound} title={S.profile.noFields} />
        ) : (
          <form className="profile-form" onSubmit={save}>
            <div className="profile-form__body psegs">
              {segments.map((segment) => (
                <fieldset className="pseg" key={segment.id}>
                  <legend className="pseg__title">
                    <bdi>{segment.title}</bdi>
                  </legend>
                  <div className="pform">{segment.rows}</div>
                </fieldset>
              ))}
            </div>

            {canEdit && <div className="profile-form__foot">
              <Button type="submit" disabled={busy}>
                <Save size={16} strokeWidth={2} />
                {S.profile.save}
              </Button>
            </div>}
          </form>
        )}
      </div>
    </>
  )
}
